<template>
  <div>
    <div
      v-if="show"
      ref="overlay"
      class="shipping-calc-overlay fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full"
      @click.self="$emit('close')"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white"
      >
        <!-- Header -->
        <div class="flex justify-between items-center pb-3 border-b">
          <h3 class="text-xl font-semibold text-gray-900">
            Shipping Calculation Details
          </h3>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            &times;
          </button>
        </div>

        <!-- Content -->
        <div v-if="calculation" class="mt-4 space-y-4">
          <!-- Summary Info -->
          <div class="bg-blue-50 p-4 rounded-lg">
            <h4 class="font-semibold text-lg mb-2">Summary</h4>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div>
                <span class="font-medium">Calculation ID:</span>
                {{ calculation.calculationId }}
              </div>
              <div>
                <span class="font-medium">Total Weight:</span>
                {{ calculation.totalWeight }} lbs
              </div>
              <div>
                <span class="font-medium">Total Boxes:</span>
                {{ calculation.totalBoxes }}
              </div>
              <div>
                <span class="font-medium">Cheapest Rate:</span>
                ${{ calculation.cheapestRate }}
              </div>
              <div>
                <span class="font-medium">Created:</span>
                {{ formatDate(calculation.createdAt) }}
              </div>
              <div>
                <span class="font-medium">Cache Hits:</span>
                {{ calculation.hitCount }}
                <span v-if="calculation.cached" class="text-green-600 ml-1">
                  (Cached)
                </span>
              </div>
              <div
                v-if="
                  calculation.hasSpecialOrder == '1' ||
                    calculation.hasSpecialOrder == 1
                "
                class="col-span-2"
              >
                <span
                  class="bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs"
                >
                  ⚠️ Special Order
                </span>
              </div>
            </div>
          </div>

          <!-- Selected Service -->
          <div
            v-if="calculation.selectedService"
            class="bg-green-50 p-4 rounded-lg"
          >
            <h4 class="font-semibold text-lg mb-2">Selected Service</h4>
            <div class="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span class="font-medium">Carrier:</span>
                {{ calculation.selectedService.carrier || "N/A" }}
              </div>
              <div>
                <span class="font-medium">Service Code:</span>
                {{ calculation.selectedService.code || "N/A" }}
              </div>
              <div>
                <span class="font-medium">Rate:</span>
                ${{ calculation.selectedService.rate || "0.00" }}
              </div>
            </div>
          </div>

          <!-- Warnings -->
          <div
            v-if="calculation.warnings && calculation.warnings.length > 0"
            class="bg-yellow-50 p-4 rounded-lg"
          >
            <h4 class="font-semibold text-lg mb-2">Warnings</h4>
            <ul class="space-y-1">
              <li
                v-for="(warning, index) in calculation.warnings"
                :key="index"
                class="text-sm"
                :class="getWarningClass(warning)"
              >
                {{ getWarningMessage(warning) }}
              </li>
            </ul>
          </div>

          <!-- Boxes -->
          <div v-if="calculation.boxes && calculation.boxes.length > 0">
            <h4 class="font-semibold text-lg mb-2">Boxes/Packages</h4>
            <div class="space-y-2">
              <div
                v-for="box in calculation.boxes"
                :key="box.boxNumber"
                class="border rounded-lg p-3 bg-gray-50"
              >
                <div class="flex justify-between items-center mb-2">
                  <span class="font-medium">Box #{{ box.boxNumber }}</span>
                  <span class="text-sm text-gray-600">
                    Source: {{ box.shippingSource }}
                    <template v-if="box.warehouseName">
                      - {{ box.warehouseName }}
                    </template>
                  </span>
                </div>
                <div class="grid grid-cols-4 gap-2 text-sm text-gray-700">
                  <div>
                    <span class="font-medium">Weight:</span>
                    {{ box.weight }} lbs
                  </div>
                  <div class="col-span-2">
                    <span class="font-medium">Dimensions:</span>
                    {{ box.dimensions.length }}" x {{ box.dimensions.width }}" x
                    {{ box.dimensions.height }}"
                  </div>
                  <div>
                    <span class="font-medium">Items:</span>
                    {{ box.items.length }}
                  </div>
                </div>
                <!-- Box Items -->
                <div v-if="box.items && box.items.length > 0" class="mt-2">
                  <details class="text-sm">
                    <summary
                      class="cursor-pointer text-blue-600 hover:text-blue-800"
                    >
                      View items ({{ box.items.length }})
                    </summary>
                    <ul class="mt-2 space-y-1 ml-4">
                      <li v-for="(item, idx) in box.items" :key="idx">
                        {{ item.sku }} - Qty: {{ item.quantity }}
                        <span v-if="item.weight" class="text-gray-500">
                          ({{ item.weight }} lbs)
                        </span>
                      </li>
                    </ul>
                  </details>
                </div>
              </div>
            </div>
          </div>

          <!-- Local Delivery Options -->
          <div
            v-if="localDeliveryOptions.length > 0"
            class="bg-purple-50 p-4 rounded-lg"
          >
            <h4 class="font-semibold text-lg mb-2">
              Local Delivery Options
            </h4>
            <div class="overflow-x-auto">
              <table class="rates-table min-w-full text-sm">
                <thead class="bg-purple-100">
                  <tr>
                    <th class="px-4 py-2 text-left">Deposit</th>
                    <th class="px-4 py-2 text-left">Type</th>
                    <th class="px-4 py-2 text-right">Distance (mi)</th>
                    <th class="px-4 py-2 text-right">Price</th>
                    <th class="px-4 py-2 text-center">Eligible</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(opt, idx) in localDeliveryOptions"
                    :key="`ld-${idx}`"
                    class="border-b hover:bg-purple-50"
                  >
                    <td class="px-4 py-2">
                      {{ opt.depositoName || opt.depositoId }}
                    </td>
                    <td class="px-4 py-2">
                      <span
                        class="px-2 py-1 rounded text-xs"
                        :class="
                          opt.type === 'local_zip'
                            ? 'bg-green-200 text-green-800'
                            : 'bg-yellow-200 text-yellow-800'
                        "
                      >
                        {{ opt.type }}
                      </span>
                    </td>
                    <td class="px-4 py-2 text-right">
                      {{ opt.distanceMiles }}
                    </td>
                    <td class="px-4 py-2 text-right">${{ opt.price }}</td>
                    <td class="px-4 py-2 text-center">
                      <span v-if="opt.eligible" class="text-green-600">✓</span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Shipping Rates -->
          <div v-if="ratesList.length > 0">
            <h4 class="font-semibold text-lg mb-2">Available Shipping Rates</h4>
            <div class="rates-scroll overflow-x-auto">
              <table class="rates-table min-w-full text-sm">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="px-4 py-2 text-right">Rate</th>
                    <th class="px-4 py-2 text-left">Carrier</th>
                    <th class="px-4 py-2 text-left">Service</th>
                    <th class="px-4 py-2 text-left">Package</th>
                    <th class="px-4 py-2 text-center">Delivery Days</th>
                    <th class="px-4 py-2 text-center">Est. Delivery</th>
                    <th class="px-4 py-2 text-center">Ship Date</th>
                    <th class="px-4 py-2 text-center">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(rate, index) in ratesList"
                    :key="index"
                    class="border-b hover:bg-gray-50 align-top"
                    :class="{
                      'bg-green-50': isSelectedRate(rate)
                    }"
                  >
                    <td class="px-4 py-2 text-right">
                      <div class="font-medium">
                        ${{ rate.price || rate.shippingAmount || "0.00" }}
                      </div>
                      <div
                        v-if="rateBreakdown(rate)"
                        class="text-xs text-gray-500"
                      >
                        {{ rateBreakdown(rate) }}
                      </div>
                    </td>
                    <td class="px-4 py-2">
                      {{
                        rate.company ||
                          rate.carrier ||
                          rate.carrierFriendlyName ||
                          "N/A"
                      }}
                    </td>
                    <td class="rates-service px-4 py-2">
                      <div>{{ rate.service || rate.serviceType || "N/A" }}</div>
                      <div
                        v-if="rate.serviceCode || rate.code"
                        class="text-xs text-gray-500"
                      >
                        {{ rate.serviceCode || rate.code }}
                      </div>
                    </td>
                    <td class="px-4 py-2">
                      {{ rate.packageType || rate.packageType || "—" }}
                    </td>
                    <td class="px-4 py-2 text-center">
                      <div v-if="deliveryDaysNumber(rate)" class="font-medium">
                        {{ deliveryDaysNumber(rate) }}
                      </div>
                      <div
                        v-if="
                          rate.carrierDeliveryDays || rate.carrier_delivery_days
                        "
                        class="text-xs text-gray-500"
                      >
                        {{
                          rate.carrierDeliveryDays || rate.carrier_delivery_days
                        }}
                      </div>
                      <div
                        v-if="
                          !deliveryDaysNumber(rate) &&
                            !(
                              rate.carrierDeliveryDays ||
                              rate.carrier_delivery_days
                            )
                        "
                        class="text-gray-400"
                      >
                        —
                      </div>
                    </td>
                    <td class="px-4 py-2 text-center">
                      {{
                        formatShortDate(
                          rate.estimatedDeliveryDate ||
                            rate.estimated_delivery_date
                        )
                      }}
                    </td>
                    <td class="px-4 py-2 text-center">
                      {{ formatShortDate(rate.shipDate || rate.ship_date) }}
                    </td>
                    <td
                      class="px-4 py-2 text-center space-x-1 whitespace-nowrap"
                    >
                      <span
                        v-if="rate.guaranteedService || rate.guaranteed_service"
                        class="bg-green-200 text-green-800 px-1.5 py-0.5 rounded text-xs"
                        title="Guaranteed delivery"
                        >G</span
                      >
                      <span
                        v-if="rate.trackable"
                        class="bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded text-xs"
                        title="Trackable"
                        >T</span
                      >
                      <span
                        v-if="rate.negotiatedRate || rate.negotiated_rate"
                        class="bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded text-xs"
                        title="Negotiated rate"
                        >N</span
                      >
                      <span
                        v-if="
                          rate.validationStatus &&
                            rate.validationStatus !== 'valid'
                        "
                        class="bg-yellow-200 text-yellow-800 px-1.5 py-0.5 rounded text-xs"
                        :title="`Validation: ${rate.validationStatus}`"
                        >!</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="text-xs text-gray-500 mt-2">
                <span class="mr-3"><b>G</b> Guaranteed</span>
                <span class="mr-3"><b>T</b> Trackable</span>
                <span class="mr-3"><b>N</b> Negotiated</span>
                <span><b>!</b> Validation warning</span>
              </div>
            </div>
          </div>

          <!-- Shipping Address -->
          <div class="bg-gray-50 p-4 rounded-lg">
            <h4 class="font-semibold text-lg mb-2">Shipping Address</h4>
            <div class="grid grid-cols-3 gap-2 text-sm">
              <div>
                <span class="font-medium">Postal Code:</span>
                {{ calculation.shippingAddress.postalCode }}
              </div>
              <div>
                <span class="font-medium">State:</span>
                {{ calculation.shippingAddress.state }}
              </div>
              <div>
                <span class="font-medium">Country:</span>
                {{ calculation.shippingAddress.country }}
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-6 flex justify-end">
          <button
            @click="$emit('close')"
            class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ShippingCalculationModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    calculation: {
      type: Object,
      default: null
    }
  },
  watch: {
    // Se monta dentro del modal de la orden (BaseModal, z-index 100), que
    // crea su propio contexto de apilamiento: mientras está abierto, el
    // overlay se mueve al body para quedar por encima del header de la orden.
    show: {
      immediate: true,
      handler(open) {
        if (open) {
          this.$nextTick(() => {
            const overlay = this.$refs.overlay;
            if (overlay && overlay.parentNode !== document.body) {
              document.body.appendChild(overlay);
            }
          });
        } else {
          // Corre antes del re-render: se devuelve a su lugar para que Vue
          // lo quite de donde lo puso.
          this.restoreOverlay();
        }
      }
    }
  },
  beforeDestroy() {
    this.restoreOverlay();
  },
  computed: {
    ratesList() {
      if (!this.calculation) return [];
      return (
        this.calculation.rates ||
        this.calculation.shippingOptions ||
        this.calculation.shipping_options ||
        []
      );
    },
    localDeliveryOptions() {
      if (!this.calculation) return [];
      const raw =
        this.calculation.localDeliveryOptions ||
        this.calculation.local_delivery_options ||
        [];
      return raw.map(o => ({
        depositoId: o.depositoId || o.deposito_id,
        depositoName: o.depositoName || o.deposito_name,
        distanceMiles: o.distanceMiles || o.distance_miles,
        type: o.type,
        price: o.price,
        eligible: o.eligible
      }));
    }
  },
  methods: {
    restoreOverlay() {
      const overlay = this.$refs.overlay;
      if (overlay && overlay.parentNode === document.body) {
        this.$el.appendChild(overlay);
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    formatShortDate(dateString) {
      if (!dateString) return "—";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    },
    deliveryDaysLabel(rate) {
      const days = rate.deliveryDays || rate.delivery_days;
      const carrierDays =
        rate.carrierDeliveryDays || rate.carrier_delivery_days;
      if (days) return `${days} day${days > 1 ? "s" : ""}`;
      if (carrierDays) return carrierDays;
      return "N/A";
    },
    deliveryDaysNumber(rate) {
      const days = rate.deliveryDays || rate.delivery_days;
      if (!days) return null;
      return `${days} day${days > 1 ? "s" : ""}`;
    },
    rateBreakdown(rate) {
      const parts = [];
      const shipping = rate.shippingAmount || rate.shipping_amount;
      const insurance = rate.insuranceAmount || rate.insurance_amount;
      const confirmation = rate.confirmationAmount || rate.confirmation_amount;
      const other = rate.otherAmount || rate.other_amount;
      if (shipping && Number(shipping) > 0) parts.push(`Ship $${shipping}`);
      if (insurance && Number(insurance) > 0) parts.push(`Ins $${insurance}`);
      if (confirmation && Number(confirmation) > 0)
        parts.push(`Conf $${confirmation}`);
      if (other && Number(other) > 0) parts.push(`Other $${other}`);
      return parts.length > 1 ? parts.join(" + ") : "";
    },
    isSelectedRate(rate) {
      if (!this.calculation || !this.calculation.selectedService) return false;
      return (
        rate.serviceCode === this.calculation.selectedService.code ||
        rate.code === this.calculation.selectedService.code
      );
    },
    getWarningClass(warning) {
      const classes = {
        special_order: "text-orange-700",
        shipped_from_another_warehouse: "text-blue-700",
        not_all_products_in_stock: "text-yellow-700"
      };
      return classes[warning] || "text-gray-700";
    },
    getWarningMessage(warning) {
      const messages = {
        special_order:
          "⚠️ Special Order: One or more products need to be ordered from supplier (Department B)",
        shipped_from_another_warehouse:
          "📦 This shipment will be fulfilled from another warehouse that has stock",
        not_all_products_in_stock: "⚠️ Some products are not currently in stock"
      };
      return messages[warning] || warning;
    }
  }
};
</script>

<style scoped>
details summary::-webkit-details-marker {
  display: none;
}

/* Por encima del modal de la orden (BaseModal, z-index 100). */
.shipping-calc-overlay {
  z-index: 1000;
}

/* En pantallas angostas la tabla hace scroll horizontal en vez de partir
   el texto letra por letra; sólo el servicio (nombre largo) puede partirse.
   Los !important y el selector de tres niveles le ganan a los estilos
   globales de mobile de _base-order-modal (.box table / .order-content td),
   que fuerzan table-layout: fixed, width: 100% y white-space: normal. */
.rates-scroll .rates-table {
  table-layout: auto !important;
  width: auto !important;
  min-width: 100% !important;
  max-width: none !important;
}
.rates-scroll .rates-table th,
.rates-scroll .rates-table td {
  white-space: nowrap !important;
  word-break: normal !important;
  overflow-wrap: normal !important;
}
.rates-scroll .rates-table .rates-service {
  white-space: normal !important;
  min-width: 14rem !important;
}
</style>
